import { getFunctions, httpsCallable } from 'firebase/functions';
import { ComicFormat, TrackBy } from './types';

export type SearchSource = 'metron' | 'comicvine' | 'openlibrary';

// One result from the comicsSearch function. Metron and Comic Vine give
// volumes (a series run, or a one-shot) and issues; Open Library gives books.
export interface ComicSearchResult {
  source: SearchSource;
  id: string;
  kind: 'volume' | 'issue' | 'book';
  title: string;
  year: number | null;
  coverUrl: string;
  thumbUrl: string;
  publisher?: string;
  // Volumes: number of issues (or of volumes, for a trade series)
  count?: number;
  // Metron volumes: "Trade Paperback", "Limited Series", "One-Shot"…
  seriesType?: string;
  ended?: boolean;
  // Issues
  number?: number | null;
  seriesTitle?: string;
  seriesExternalId?: string | null;
  // Books
  writers?: string[];
}

export interface ComicSearchResponse {
  source: SearchSource;
  results: ComicSearchResult[];
}

// Fetched once a result is picked: covers and credits aren't in search results.
export interface ComicSearchDetail {
  coverUrl?: string;
  publisher?: string;
  seriesType?: string;
  storyTitle?: string;
  writers: string[];
  artists: string[];
}

export const SOURCE_NAMES: Record<SearchSource, string> = {
  metron: 'Metron',
  comicvine: 'Comic Vine',
  openlibrary: 'Open Library',
};

const callSearch = () => httpsCallable(getFunctions(), 'comicsSearch');

export const searchComics = async (query: string, kinds: 'all' | 'volume' = 'all') => {
  const { data } = await callSearch()({ query, kinds });
  return data as ComicSearchResponse;
};

// Only Metron and Comic Vine have details; Open Library results are complete.
export const hasDetail = (result: ComicSearchResult) =>
  result.source === 'metron' || (result.source === 'comicvine' && result.kind === 'issue');

export const fetchDetail = async (result: ComicSearchResult) => {
  const { data } = await callSearch()({
    detail: { source: result.source, kind: result.kind, id: result.id },
  });
  return data as ComicSearchDetail;
};

// Every issue cover in a series at its source, keyed by number
export const fetchSeriesIssueCovers = async (externalId: { source: string; id: string }) => {
  const { data } = await callSearch()({ seriesIssues: externalId });
  const { issues } = data as { issues: { number: number | null; coverUrl: string }[] };
  return new Map(
    issues
      .filter((issue) => issue.number != null)
      .map((issue) => [issue.number as number, issue.coverUrl])
  );
};

// "Saga TPB (2012)" → { name: "Saga", year: 2012 }
export const splitSeriesTitle = (title?: string) => {
  const year = title?.match(/\((\d{4})\)\s*$/)?.[1];
  return {
    name: (title ?? '')
      .replace(/\s*\(\d{4}\)\s*$/, '')
      .replace(/\s+(TPB|HC|GN|OHC|Omnibus)$/i, '')
      .trim(),
    year: year ? Number(year) : null,
  };
};

/* Metron series types → how the page stores things */
const COLLECTED_TYPES = ['Trade Paperback', 'Hardcover', 'Omnibus'];

export const isCollectedType = (type?: string) => COLLECTED_TYPES.includes(type ?? '');

export const formatForSeriesType = (type?: string): ComicFormat | null => {
  switch (type) {
    case 'Trade Paperback':
      return 'trade';
    case 'Hardcover':
      return 'hardcover';
    case 'Omnibus':
      return 'omnibus';
    case 'Graphic Novel':
      return 'graphicNovel';
    case 'Single Issue':
    case 'Limited Series':
    case 'One-Shot':
    case 'Annual':
    case 'Digital Chapter':
      return 'issue';
    default:
      return null;
  }
};

export const trackByForType = (type?: string): TrackBy =>
  type && !isCollectedType(type) && type !== 'Graphic Novel' ? 'issue' : 'volume';

// A volume that is really one book: a one-shot, a graphic novel, a single trade.
export const isSingleBook = (result: ComicSearchResult) =>
  result.kind === 'volume' &&
  ((result.count ?? 0) <= 1 || result.seriesType === 'Graphic Novel' || result.seriesType === 'One-Shot');

// A short line under each result: what it is and where it's from.
export const resultDetail = (result: ComicSearchResult) => {
  if (result.kind === 'issue') {
    return [result.seriesTitle, result.year].filter(Boolean).join(' · ');
  }
  if (result.kind === 'volume') {
    const unit = isCollectedType(result.seriesType) ? 'vols' : 'issues';
    const size = isSingleBook(result)
      ? result.seriesType || 'One-shot'
      : [result.seriesType || 'Series', result.count ? `${result.count} ${unit}` : '']
          .filter(Boolean)
          .join(' · ');
    return [size, result.publisher, result.year].filter(Boolean).join(' · ');
  }
  return [result.writers?.[0], result.publisher, result.year].filter(Boolean).join(' · ');
};
